import React, { useEffect, useRef, useState } from "react";
import { formatDate, parseDisplayDate } from "../../utils/dateFormat";

// Date box shown as dd-mm-yyyy (Buddhist-era year) on every browser and device, instead of the browser's own
// mm/dd/yyyy. Drop-in for <input type="date">: `value` is ISO yyyy-mm-dd and onChange gets { target: { value } }
// with an ISO value ("" while the typed text is not a complete, real date).
function DateInput({ value, onChange, className = "", required, disabled, id, name, ...rest }) {
  const [text, setText] = useState(formatDate(value));
  const [invalid, setInvalid] = useState(false);
  const lastIso = useRef(value || "");
  const picker = useRef(null);

  // a new value from outside (form reset, another record opened) replaces what is shown
  useEffect(() => {
    if ((value || "") !== lastIso.current) {
      lastIso.current = value || "";
      setText(formatDate(value));
      setInvalid(false);
    }
  }, [value]);

  const emit = (iso) => {
    lastIso.current = iso;
    if (onChange) onChange({ target: { value: iso, name } });
  };

  const onType = (e) => {
    // keep digits only and put the dashes in: 25102569 → 25-10-2569
    const digits = e.target.value.replace(/\D/g, "").slice(0, 8);
    const next = [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4)].filter(Boolean).join("-");
    setText(next);
    const iso = parseDisplayDate(next);
    setInvalid(digits.length === 8 && !iso);
    if ((iso || "") !== lastIso.current) emit(iso || "");
  };

  const onPick = (e) => {
    const iso = e.target.value;
    setText(formatDate(iso));
    setInvalid(false);
    emit(iso);
  };

  const openPicker = () => {
    try { picker.current?.showPicker(); } catch (err) { picker.current?.focus(); }
  };

  return (
    <span className={`mng-date${invalid ? " invalid" : ""}${disabled ? " disabled" : ""}`}>
      <input
        {...rest}
        id={id}
        name={name}
        className={className}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        placeholder="dd-mm-yyyy"
        maxLength={10}
        value={text}
        onChange={onType}
        onBlur={() => setInvalid(text !== "" && !parseDisplayDate(text))}
        required={required}
        disabled={disabled}
        aria-invalid={invalid || undefined}
        title="วัน-เดือน-ปี พ.ศ. เช่น 25-10-2569"
      />
      <span className="mng-date-button">
        <svg aria-hidden="true" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.6">
          <rect x="3.5" y="5" width="17" height="15" rx="1.5" />
          <path d="M3.5 9.5h17M8 3v4M16 3v4" />
        </svg>
        {/* the real browser date picker, invisible on top of the icon (tapping it opens the picker on phones too) */}
        <input
          ref={picker}
          type="date"
          tabIndex={-1}
          value={value || ""}
          onChange={onPick}
          onClick={openPicker}
          disabled={disabled}
          aria-label="เลือกวันที่จากปฏิทิน"
        />
      </span>
    </span>
  );
}

export default DateInput;
